import { useMemo } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { omit } from 'lodash-es';
import { ScheduleRequestEvent, ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { PRIMARY_EVENT_COLOR, REQUEST_EVENT_BACKGROUND_COLOR } from '../../constants';
import { convertScheduleOpeningToEventTime } from '../../utils';
import { getScheduleRequestEventId } from '../../utils/calendar-helpers';
import { useRequestedAppointmentTypes } from './use-requested-appointment-types';

export const useConvertScheduleRequestToEvent = (scheduleRequests: ScheduleRequest[]) => {
  const { requestedAppointmentTypes } = useRequestedAppointmentTypes(scheduleRequests);

  const scheduleRequestEventsData: ScheduleRequestEvent[] = useMemo(
    () =>
      scheduleRequests
        ?.map((request) => convertRequestToScheduleRequestEvent(request, requestedAppointmentTypes))
        .flat() || [],
    [scheduleRequests, requestedAppointmentTypes]
  );

  return { scheduleRequestEventsData, requestedAppointmentTypes };
};

export const convertRequestToScheduleRequestEvent = (
  request: ScheduleRequest,
  appointmentTypes?: AppointmentType[]
) => {
  const appointmentType = appointmentTypes?.find((type) => type.id === request.appointmentTypeId);
  const { id, practitionerId, requestedOpenings, ...restRequestData } = request;
  const requestedProviderId = practitionerId;
  const convertedOpenings =
    requestedOpenings?.map((opening) => convertScheduleOpeningToEventTime(opening, request.duration ?? 0)) ?? [];
  return (requestedOpenings || []).map((opening, index) => {
    const requestedProviderName = opening.assets?.providers?.[0];
    const operatoryId = opening.assets?.operatories?.[0];
    const thisOpening = convertedOpenings?.[index];
    const otherRequestedOpenings = convertedOpenings?.filter((_, i) => i !== index) ?? [];

    return {
      id: getScheduleRequestEventId(id ?? '', index),
      requestId: id,
      ...restRequestData,
      organizerId: requestedProviderId,
      providerName: requestedProviderName,
      referenceTypeId: request.appointmentTypeId,
      operatoryId,
      appointmentTypeName: appointmentType?.displayName || '',
      ...thisOpening,
      displayOptions: {
        primaryColor: appointmentType?.details?.clr || PRIMARY_EVENT_COLOR,
        backgroundColor: REQUEST_EVENT_BACKGROUND_COLOR,
      },
      request: {
        ...request,
        id: getScheduleRequestEventId(id, index),
        requestId: id,
        allRequestedOpenings: convertedOpenings,
        otherRequestedOpenings,
        appointmentTypeDetails: appointmentType,
      },
      type: 'request' as const,
    };
  });
};

export const convertScheduleRequestEventToRequest = (
  requestData: ScheduleRequestEventData | null
): ScheduleRequest | null => {
  if (!requestData) return null;

  const scheduleRequest = omit(requestData, [
    'requestId',
    'allRequestedOpenings',
    'otherRequestedOpenings',
    'appointmentTypeDetails',
  ]);
  return {
    ...scheduleRequest,
    id: requestData.requestId,
  };
};
